import { FormEvent, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ReactNode } from "react";
import {
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
} from "@material-ui/core";
import SettingsIcon from "@mui/icons-material/Settings";
import CardMembershipIcon from "@mui/icons-material/CardMembership";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import RuleIcon from "@mui/icons-material/Rule";
import { editCustomer, getCustomer } from "@State/index";
import ModalLayout from "@View/components/ModalLayout";
import DefaultInput from "@View/components/formInputs/DefaultInput";
import EmailChipsInput from "@View/components/formInputs/EmailChipsInput";
import SubscriptionSection from "./SubscriptionSection";
import StopFactorsSettingsSection from "./StopFactorsSettingsSection";
import ButtonSave from "@View/components/buttons/ButtonSave";
import { getRoleSafe } from "@Security/stateHelpers";
import { getStopFactorSettings, updateStopFactorSettings } from "@Dao/stopFactorsApi";
import { StopFactorSetting } from "@Constants/types";

const useStyles = makeStyles(() => ({
  sidebar: {
    width: "220px",
    padding: "12px",
    boxSizing: "border-box",
  },
  listItem: {
    borderRadius: "6px",
    marginBottom: "8px",
    border: "1px solid #E1E6F1",
    "&:hover": {
      backgroundColor: "#E1E6F1",
    },
    "&.active": {
      backgroundColor: "#FFFFFF",
      borderColor: "#FFFFFF",
      "& .MuiListItemText-primary": {
        fontWeight: 600,
        color: "#1A1E24",
      },
      "& $listItemIcon": {
        color: "#1A1E24",
      },
    },
    "&:not(.active)": {
      backgroundColor: "#F3F5F7",
    },
  },
  listItemIcon: {
    minWidth: "32px",
    color: "#54606F",
    "& .MuiSvgIcon-root": {
      fontSize: "20px",
    },
  },
  listItemText: {
    "& .MuiListItemText-primary": {
      fontSize: "16px",
      fontWeight: 500,
      color: "#1A1E24",
    },
  },
}));

type SectionId = "general" | "subscription" | "leadGen" | "stopFactors";

const sections: { id: SectionId; label: string; icon: ReactNode }[] = [
  { id: "general", label: "customerSettings.general", icon: <SettingsIcon /> },
  {
    id: "subscription",
    label: "customerSettings.subscription",
    icon: <CardMembershipIcon />,
  },
  {
    id: "leadGen",
    label: "customerSettings.leadGen",
    icon: <PersonSearchIcon />,
  },
  {
    id: "stopFactors",
    label: "customerSettings.stopFactors",
    icon: <RuleIcon />,
  },
];

/** CustomerLeadRole приходит строкой (StringEnumConverter на бэке). */
const LEAD_ROLES = ["LeadGenerator", "LeadReceiver"];

type CustomerSettingsModalProps = {
  open: boolean;
  handleClose: () => void;
  customerId: string | number;
  /** Раздел, который открыт при показе модалки */
  initialSection?: SectionId;
};

const CustomerSettingsModal = ({
  open,
  handleClose,
  customerId,
  initialSection = "general",
}: CustomerSettingsModalProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();

  const customer: any = useSelector((state: any) => state.customer.customer);
  const [newData, setNewData] = useState<any>(null);
  const [activeSection, setActiveSection] = useState<SectionId>(initialSection);
  const [stopFactorSettings, setStopFactorSettings] = useState<StopFactorSetting[] | null>(null);

  const isAdmin = getRoleSafe() === "Admin";

  useEffect(() => {
    if (window.location.href.indexOf("customer/details") === -1 && open === true) {
      dispatch(getCustomer(customerId));
    }
  }, [customerId, open, dispatch]);

  useEffect(() => {
    if (customer && Object.keys(customer).length > 0) {
      setNewData({ ...customer });
    }
  }, [customer]);

  useEffect(() => {
    if (open) {
      setActiveSection(initialSection);
    }
  }, [open, initialSection]);

  useEffect(() => {
    setStopFactorSettings(null);
    if (!open || !isAdmin || activeSection !== "stopFactors") return;

    let cancelled = false;
    getStopFactorSettings(String(customerId))
      .then((settings) => {
        if (!cancelled) setStopFactorSettings(settings);
      })
      .catch((e) => console.error("[getStopFactorSettings]", e));

    return () => {
      cancelled = true;
    };
  }, [open, isAdmin, activeSection, customerId]);

  const handleChange = (value: any, label: string) => {
    setNewData({ ...newData, [label]: value });
  };

  const handleStopFactorChange = (key: string, isEnabled: boolean) => {
    setStopFactorSettings((prev) =>
      prev?.map((s) => (s.key === key ? { ...s, isEnabled } : s)) ?? prev
    );
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (currentSection === "stopFactors") {
      if (!stopFactorSettings) return;
      const enabledKeys = stopFactorSettings.filter((s) => s.isEnabled).map((s) => s.key);
      updateStopFactorSettings(String(customerId), enabledKeys)
        .then(setStopFactorSettings)
        .catch((e) => console.error("[updateStopFactorSettings]", e));
      return;
    }
    // Модалку не закрываем: настройки многораздельные, после сохранения можно
    // продолжить в соседней вкладке. Об успехе сообщает снекбар из editCustomer
    dispatch(editCustomer(newData, history));
  };

  if (newData === undefined || newData === null) return <></>;

  const visibleSections = sections.filter(
    (section) =>
      (section.id !== "leadGen" || LEAD_ROLES.includes(newData.customerRole)) &&
      (section.id !== "stopFactors" || isAdmin)
  );

  // Данные заказчика приезжают уже после открытия модалки, поэтому активный раздел
  // может оказаться скрытым — в этом случае показываем «Общее»
  const currentSection = visibleSections.some((s) => s.id === activeSection)
    ? activeSection
    : "general";

  const generalSection = (
    <>
      <DefaultInput
        value={newData.name}
        label="inputs.CompanyName"
        onChange={(value: string) => handleChange(value, "name")}
      />
      <DefaultInput
        value={newData.externalId}
        label="inputs.External Id"
        onChange={(value: string) => handleChange(value, "externalId")}
      />
      <DefaultInput
        value={newData.email}
        label="inputs.Email"
        onChange={(value: string) => handleChange(value, "email")}
      />
      <DefaultInput
        value={newData.oneSPackageName}
        label="inputs.OneSPackageName"
        onChange={(value: string) => handleChange(value, "oneSPackageName")}
      />
    </>
  );

  const leadGenSection = (
    <EmailChipsInput
      label="inputs.leadNotificationEmails"
      value={newData.leadNotificationEmails ?? []}
      onChange={(emails: string[]) =>
        handleChange(emails, "leadNotificationEmails")
      }
    />
  );

  const sectionContent =
    currentSection === "general"
      ? generalSection
      : currentSection === "leadGen"
        ? leadGenSection
        : currentSection === "stopFactors"
          ? <StopFactorsSettingsSection settings={stopFactorSettings} onChange={handleStopFactorChange} />
          : <SubscriptionSection customer={newData} />;

  // «Подписка» — раздел только на чтение, сохранять там нечего
  const showSave = currentSection !== "subscription";

  const sidebar = (
    <div className={classes.sidebar}>
      <List disablePadding>
        {visibleSections.map((section) => (
          <ListItem
            key={section.id}
            button
            className={`${classes.listItem} ${currentSection === section.id ? "active" : ""
              }`}
            onClick={() => setActiveSection(section.id)}
          >
            <ListItemIcon className={classes.listItemIcon}>
              {section.icon}
            </ListItemIcon>
            <ListItemText
              primary={t(section.label)}
              className={classes.listItemText}
            />
          </ListItem>
        ))}
      </List>
    </div>
  );

  const footer = showSave ? (
    <ButtonSave marginTop="0" marginBottom="0">
      buttons.Save
    </ButtonSave>
  ) : null;

  return (
    <ModalLayout
      open={open}
      onClose={handleClose}
      title="h1.Customer Settings"
      subtitle={newData.name}
      sidebar={sidebar}
      maxWidth="720px"
      verticalInset="60px"
      onSubmit={handleSubmit}
      footer={footer}
    >
      {sectionContent}
    </ModalLayout>
  );
};

export default CustomerSettingsModal;
